"use client";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useDict } from "@/app/[lang]/providers/DictionaryProvider";
import { HomePage } from "../type/type";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function Services({ data }: { data: HomePage | undefined }) {
  const dict = useDict();
  const router = useRouter();
  const onClickServiceCta = () => {
    if (data?.service_cta_url) {
      router.push(data.service_cta_url);
    }
  };

  return (
    <div className="w-full bg-primary-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-0 py-16 md:py-30 flex flex-col lg:flex-row items-center gap-10 relative overflow-hidden">
        <Image
          src="/images/bg-services.png"
          alt="SMF Services"
          width={581}
          height={387}
          className="pointer-events-none absolute w-[300px] h-auto md:w-[581px] md:h-auto z-[0] right-0 top-3 opacity-60 md:opacity-100"
        />
        <div className="lg:w-1/4 z-10">
          <h2 className="font-bold text-3xl md:text-5xl text-negatif mb-2">
            {data?.service_title || dict.homepage?.service_title || "Services"}
          </h2>
          <div className="text-negatif text-base leading-relaxed">
            <div dangerouslySetInnerHTML={{ __html: data?.service_desc ?? "" }} />
          </div>
          {data?.service_cta_text && (
            <button
              onClick={onClickServiceCta}
              className="mt-6 w-fit text-base md:text-lg px-6 py-3 md:px-10 bg-primary-300 text-primary-800 font-semibold rounded-lg hover:bg-white transition-colors"
            >
              {data.service_cta_text}
            </button>
          )}
        </div>

        <div className="lg:w-3/4 w-full relative">
          {/* Custom Navigation Button */}
          <button
            type="button"
            aria-label="Previous service"
            className="prev-btn absolute left-0 md:-left-8 top-1/2 z-10 -translate-y-1/2 bg-primary-300 border-4 border-primary-800 rounded-full p-1 shadow hover:bg-gray-100"
          >
            <ChevronLeft className="w-6 h-6 text-primary-800" />
          </button>
          <button
            type="button"
            aria-label="Next service"
            className="next-btn absolute right-0 md:-right-6 top-1/2 z-10 -translate-y-1/2 bg-primary-300 border-4 border-primary-800 rounded-full p-1 shadow hover:bg-gray-100"
          >
            <ChevronRight className="w-6 h-6 text-primary-800" />
          </button>

          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={16}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            navigation={{
              prevEl: ".prev-btn",
              nextEl: ".next-btn",
            }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            loop={true}
            className="w-full"
          >
            {data?.service_list?.map((item, index) => (
              <SwiperSlide key={index}>
                <div className="flex p-4 items-center bg-gray-25 w-full h-[145px] rounded-2xl shadow">
                  <Image
                    src={item.icon ? `${item.icon}` : `/images/services/default.png`}
                    alt={item.title}
                    width={60}
                    height={60}
                    className="max-w-[60px] max-h-[60px] w-auto h-auto object-contain"
                  />
                  <div className="ml-4">
                    {item.url ? (
                      <Link href={item.url} className="font-semibold text-lg text-tertiary-900 hover:underline">
                        {item.title}
                      </Link>
                    ) : (
                      <p className="font-semibold text-lg text-tertiary-900">{item.title}</p>
                    )}
                    <div className="text-[14px] [&_p]:!text-[14px] text-secondary" dangerouslySetInnerHTML={{ __html: item.description }} />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
}
